<template>
  <div
    class="ig-datepicker"
    :class="{
      'is-disabled': disabled,
      'is-placeholder': !text,
      'is-focus': focused,
    }"
    @click="open"
  >
    <span class="ig-datepicker__value">{{ displayText }}</span>
    <i
      v-if="text && !disabled"
      class="ig-datepicker__clear el-icon-circle-close"
      @click.stop="clearValue"
    ></i>
    <i class="el-icon-date ig-datepicker__icon"></i>
    <van-popup
      v-model="visible"
      position="bottom"
      get-container="body"
      close-on-click-overlay
      :lazy-render="false"
      @opened="onPopupOpened"
      @closed="onPopupClosed"
    >
      <!-- 有长期选项时才显示自定义工具栏，否则用 van-datetime-picker 自带的 -->
      <div v-if="showLongTerm" class="ig-dp-toolbar">
        <span class="ig-dp-btn" @click="onCancel">取消</span>
        <span class="ig-dp-title">
          {{ isLongTerm ? longTermLabel : "选择日期" }}
        </span>
        <span class="ig-dp-btn ig-dp-btn--primary" @click="onConfirmClick">
          确定
        </span>
      </div>
      <!-- 长期选项 -->
      <div
        v-if="showLongTerm"
        class="ig-dp-long-term"
        :class="{ 'is-active': isLongTerm }"
        @click="toggleLongTerm"
      >
        <i class="check-box" :class="{ checked: isLongTerm }"></i>
        <span>{{ longTermLabel }}</span>
      </div>
      <van-datetime-picker
        ref="picker"
        type="date"
        v-model="currentDate"
        :min-date="minDate"
        :max-date="maxDate"
        :show-toolbar="!showLongTerm"
        @confirm="onConfirmClick"
        @cancel="onCancel"
      />
    </van-popup>
  </div>
</template>

<script>
const MIN_YEAR = 1900;
// 最大年份动态扩展：当前年份 + 100，覆盖身份证长期/远期场景
const MAX_YEAR_OFFSET = 100;

const TODAY = new Date();

function parseYMD(value) {
  if (!value) return new Date(TODAY);
  const parts = String(value).split("-").map(Number);
  const [y, m, d] = parts;
  if (!y || !m || !d) return new Date(TODAY);
  return new Date(y, m - 1, d);
}

function formatYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default {
  name: "MobileDatePicker",
  props: {
    value: { type: [String, Number], default: "" },
    placeholder: { type: String, default: "请选择日期" },
    disabled: { type: Boolean, default: false },
    // 是否展示"长期"选项（身份证有效期场景）
    showLongTerm: { type: Boolean, default: false },
    // 选择"长期"时传给后端的值
    longTermValue: { type: String, default: "9999-01-01" },
    // "长期"的显示文案
    longTermLabel: { type: String, default: "长期" },
  },
  data() {
    return {
      visible: false,
      focused: false,
      currentDate: parseYMD(this.value),
      minDate: new Date(MIN_YEAR, 0, 1),
      maxDate: new Date(
        TODAY.getFullYear() + MAX_YEAR_OFFSET,
        11,
        31
      ),
      // 是否选中了"长期"
      isLongTerm: false,
      // 记录打开时的日期（避免取消时丢失原值）
      snapshotDate: null,
      snapshotIsLongTerm: false,
    };
  },
  computed: {
    text() {
      return this.value ? String(this.value) : "";
    },
    displayText() {
      if (this.showLongTerm && this.value === this.longTermValue) {
        return this.longTermLabel;
      }
      return this.text || this.placeholder;
    },
  },
  watch: {
    value(val) {
      this.currentDate = parseYMD(val);
      this.isLongTerm = val === this.longTermValue;
    },
    // 用户滑动日期时自动取消"长期"选中（open 时 visible=false 不会误触发）
    currentDate() {
      if (this.visible && this.isLongTerm) {
        this.isLongTerm = false;
      }
    },
  },
  methods: {
    open() {
      if (this.disabled) return;
      this.currentDate = parseYMD(this.value);
      this.isLongTerm = this.value === this.longTermValue;
      this.snapshotDate = this.currentDate;
      this.snapshotIsLongTerm = this.isLongTerm;
      this.visible = true;
    },
    onPopupOpened() {
      this.focused = true;
    },
    onPopupClosed() {
      this.focused = false;
    },
    toggleLongTerm() {
      this.isLongTerm = !this.isLongTerm;
    },
    onConfirmClick(value) {
      if (this.isLongTerm) {
        this.commitValue(this.longTermValue);
        return;
      }
      // value 来自 vant @confirm 事件(Date)；自定义按钮触发时用 v-model 同步的 currentDate
      const date =
        value instanceof Date ? value : this.currentDate;
      this.commitValue(formatYMD(date));
    },
    commitValue(val) {
      this.$emit("input", val);
      this.$emit("change", val);
      this.dispatchFormItem("el.form.change", val);
      this.visible = false;
    },
    clearValue() {
      this.$emit("input", "");
      this.$emit("change", "");
      this.dispatchFormItem("el.form.change", "");
    },
    onCancel() {
      // 取消时恢复打开时的快照，避免内部状态污染
      if (this.snapshotDate) {
        this.currentDate = this.snapshotDate;
      }
      this.isLongTerm = this.snapshotIsLongTerm;
      this.visible = false;
    },
    dispatchFormItem(eventName, ...args) {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options.name === "ElFormItem") {
          parent.$emit.apply(parent, [eventName].concat(args));
          break;
        }
        parent = parent.$parent;
      }
    },
  },
};
</script>

<!-- 主触发器样式：ig- 前缀豁免 postcss-pxtorem，用原始 px 单位 -->
<style lang="scss" scoped>
.ig-datepicker {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 34px;
  color: #333;
  transition: border-color 0.2s, box-shadow 0.2s;
  user-select: none;

  &:active {
    background: #f5f7fa;
  }

  &:hover {
    border-color: #c0c4cc;
  }

  &.is-focus {
    border-color: #1989fa;
    box-shadow: 0 0 0 2px rgba(25, 137, 250, 0.1);
  }

  &__value {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 24px;
  }

  &.is-placeholder .ig-datepicker__value {
    color: #c0c4cc;
  }

  &.is-disabled {
    background: #f5f5f5;
    cursor: not-allowed;

    .ig-datepicker__value {
      color: #bbb;
    }
  }

  &__icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #c0c4cc;
    font-size: 16px;
    pointer-events: none;
  }

  &__clear {
    position: absolute;
    right: 32px;
    top: 50%;
    transform: translateY(-50%);
    color: #c0c4cc;
    font-size: 16px;
    cursor: pointer;
    pointer-events: auto;
    transition: color 0.2s;

    &:active {
      color: #909399;
    }
  }
}
</style>

<!-- 非 scoped 样式：van-popup 的 DOM 在 body 下，scoped 不生效 -->
<style lang="scss">
.ig-dp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 16px;
  border-bottom: 1px solid #ebedf0;
  background: #f7f8fa;

  .ig-dp-btn {
    font-size: 14px;
    color: #969799;
    cursor: pointer;
    padding: 6px 8px;
    user-select: none;

    &:active {
      opacity: 0.6;
    }

    &--primary {
      color: #1989fa;
      font-weight: 500;
    }
  }

  .ig-dp-title {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
  }
}

.ig-dp-long-term {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  color: #323233;
  background: #fff;
  border-bottom: 1px solid #ebedf0;
  cursor: pointer;
  user-select: none;

  &:active {
    background: #f2f3f5;
  }

  &.is-active {
    color: #1989fa;
  }

  .check-box {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 8px;
    border: 1px solid #c0c4cc;
    border-radius: 3px;
    box-sizing: border-box;

    &.checked {
      background: #1989fa;
      border-color: #1989fa;

      &::after {
        content: "";
        position: absolute;
        top: 2px;
        left: 5px;
        width: 4px;
        height: 8px;
        border: 2px solid #fff;
        border-top: 0;
        border-left: 0;
        transform: rotate(45deg);
      }
    }
  }
}

.van-datetime-picker {
  padding-bottom: env(safe-area-inset-bottom);
}
</style>
